/**
 * Company: SPHMedia
 * Description: Default Section Layout
 */

import { ReactElement, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AdvertisementTypeEnum } from "@app/types/enums";
import { TrimmedBreakingNewsArticle } from "@caas/types";
import Advertisement from "@components/Advertisement/Advertisement";
import PrestitialAdWrapper from "@components/Advertisement/PrestitialAdWrapper";
import Breadcrumb from "@components/Breadcrumb/Breadcrumb";
import Container from "@components/Container/Container";
import FooterSubscribeRow from "@components/Footer/FooterSubscribeRow/FooterSubscribeRow";
import Column from "@components/Grid/Column";
import Row from "@components/Grid/Row";
import Header from "@components/Header/Header";
import KeywordFilter from "@components/KeywordFilter";
import MetaTags from "@components/MetaTags/MetaTags";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import { useMostReadArticleList } from "@hooks/useMostReadArticleList";
import BreakingNews from "@pages/Section/components/BreakingNews/BreakingNews";
import MostPopular from "@pages/Section/components/MostPopular/MostPopular";
import Overview from "@pages/Section/components/Overview/Overview";
import {
  getFormattedTextForAds,
  getTermPath,
  isEntityTagType,
} from "@pages/Section/helpers";
import { sectionRequests } from "@pages/Section/Section.server";
import type { SectionDefaultProps } from "@pages/Section/types";
import { sectionNavigationItems } from "@util/constant";
import { GoogleAdsSlotFactory } from "@util/helpers";

export default function DefaultLayout({
  context: {
    data: { entity, title, overview },
  },
}: SectionDefaultProps): ReactElement {
  const { pathname } = useLocation();
  const mostPopular = useMostReadArticleList();

  const termName = entity.name;
  const termPath = getTermPath(entity) || pathname.replace("/", "");
  const slot = isEntityTagType(entity) ? "keywords" : termPath;

  const { isPrestitialEnabled, topOverlayImpressions, topOverlayValidity } =
    useGlobalAdSetting({
      targeting: [
        { key: "page", value: "listing" },
        ...(isEntityTagType(entity)
          ? [{ key: "bttags", value: getFormattedTextForAds(termName) }]
          : []),
      ],
    });

  const [breakingNews, setBreakingNews] = useState<
    TrimmedBreakingNewsArticle[]
  >([]);

  useEffect(() => {
    const callFetch = async () => {
      const trimmedBreakingNews =
        await sectionRequests.fetchTrimmedBreakingNews();
      setBreakingNews(trimmedBreakingNews);
    };

    callFetch();
  }, []);

  return (
    <div className="w-full">
      <MetaTags
        title={title}
        description={`THE BUSINESS TIMES ${termName} - Find ${termName} News & Headlines, insight and analysis in Singapore, Asia-Pacific & global markets news at The Business Times.`}
        ogType="article"
        slug={`/${termPath}`}
      />

      <Advertisement
        adUnitProps={{
          type: AdvertisementTypeEnum.LB1,
          slot: GoogleAdsSlotFactory.lb1(slot),
        }}
      />

      <PrestitialAdWrapper
        slot={GoogleAdsSlotFactory.prestitial(slot)}
        {...{ isPrestitialEnabled, topOverlayImpressions, topOverlayValidity }}
      />

      <Header mastheadTitle={termPath} />

      {termPath &&
      sectionNavigationItems[termPath] &&
      !termPath.includes("keywords") ? (
        <div className="flex-none bg-gray-250">
          <Container>
            <Breadcrumb
              rootClassName="m-auto"
              parentCategory={termPath.split("/")[0]}
              childCategory={termPath.split("/")[1]}
              categoryDataMapping={sectionNavigationItems}
            />
          </Container>
        </div>
      ) : null}

      <Container rootClassName="pt-6">
        <Row>
          <Column rootClassName="w-full lg:w-8/12 mb-6">
            <>
              {isEntityTagType(entity) ? (
                <KeywordFilter
                  tag={entity}
                  rootClassName="bg-gray-250 mb-7 p-3 inline-block"
                />
              ) : null}
            </>

            <Overview data={overview} testId={"section-listing-component"} />
          </Column>

          <Column rootClassName="w-full lg:w-4/12 mb-6">
            <div className="mb-6 lg:h-[800px]">
              <Advertisement
                rootClassName="lg:sticky lg:top-[120px]"
                adUnitProps={{
                  type: AdvertisementTypeEnum.IMU2,
                  slot: GoogleAdsSlotFactory.imu2(slot),
                }}
              />
            </div>

            <BreakingNews
              data={breakingNews}
              rootClassName="max-w-[300px] mx-auto mb-6"
            />

            <MostPopular
              data={mostPopular}
              rootClassName="max-w-[300px] mx-auto"
            />
          </Column>
        </Row>
      </Container>

      <Advertisement
        adUnitProps={{
          type: AdvertisementTypeEnum.CATFISH,
          slot: GoogleAdsSlotFactory.catfish(slot),
        }}
      />

      <FooterSubscribeRow />
    </div>
  );
}
